'use client';

import Link from 'next/link';
import type { Route } from 'next';
import { useMemo, useState } from 'react';
import type { Post } from '../../../content/posts/types';
import type { Locale } from './theme';
import { theme } from './theme';
import { formatPostDate, getBlogStrings } from './blog-strings';
import { Reveal } from './reveal';

function normalizeSearch(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function postMatches(post: Post, query: string): boolean {
  if (!query) return true;
  const haystack = normalizeSearch(
    [post.title, post.description, post.author.name, ...post.tags].join(' '),
  );
  return haystack.includes(query);
}

function PostCard({
  post,
  locale,
  index,
}: {
  post: Post;
  locale: Locale;
  index: number;
}) {
  const b = getBlogStrings(locale);

  return (
    <Reveal as="li" delay={index * 60}>
      <Link
        href={`/${locale}/posts/${post.slug}` as Route}
        className="lp-card lp-blog-card"
        style={{
          display: 'grid',
          gridTemplateRows: 'auto auto 1fr auto',
          minHeight: 310,
          background: theme.c.surface,
          border: `1px solid ${theme.c.border}`,
          borderRadius: theme.radii.lg,
          padding: '24px',
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 16,
            alignItems: 'flex-start',
            marginBottom: 18,
          }}
        >
          <div
            aria-hidden="true"
            style={{
              display: 'grid',
              placeItems: 'center',
              width: 54,
              height: 54,
              borderRadius: 16,
              background: theme.c.bgAlt,
              color: theme.c.accent,
              fontSize: 28,
              lineHeight: 1,
              flex: '0 0 auto',
            }}
          >
            {post.coverEmoji}
          </div>
          <span
            style={{
              fontFamily: theme.fonts.mono,
              fontSize: 11,
              color: theme.c.fgFaint,
              whiteSpace: 'nowrap',
              paddingTop: 4,
            }}
          >
            {post.readingTimeMin} {b.readingTime}
          </span>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            marginBottom: 14,
          }}
        >
          {post.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              style={{
                fontFamily: theme.fonts.mono,
                fontSize: 10,
                letterSpacing: 1,
                textTransform: 'uppercase',
                color: theme.c.accent,
                background: theme.c.chip,
                padding: '4px 10px',
                borderRadius: theme.radii.pill,
              }}
            >
              {tag}
            </span>
          ))}
        </div>

        <div>
          <h2
            style={{
              fontFamily: theme.fonts.display,
              fontSize: 24,
              fontWeight: 600,
              letterSpacing: 0,
              lineHeight: 1.16,
              margin: 0,
              marginBottom: 10,
              color: theme.c.fg,
            }}
          >
            {post.title}
          </h2>
          <p
            style={{
              margin: 0,
              fontFamily: theme.fonts.body,
              fontSize: 15,
              lineHeight: 1.55,
              color: theme.c.fgMuted,
            }}
          >
            {post.description}
          </p>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 14,
            alignItems: 'center',
            marginTop: 24,
            paddingTop: 18,
            borderTop: `1px solid ${theme.c.border}`,
            fontFamily: theme.fonts.mono,
            fontSize: 11,
            color: theme.c.fgFaint,
          }}
        >
          <span>{formatPostDate(post.publishedAt, locale)}</span>
          <span style={{ color: theme.c.accent }}>{b.more}</span>
        </div>
      </Link>
    </Reveal>
  );
}

function FeaturedPost({ post, locale }: { post: Post; locale: Locale }) {
  const b = getBlogStrings(locale);

  return (
    <Link
      href={`/${locale}/posts/${post.slug}` as Route}
      className="lp-card lp-blog-featured"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1.15fr) minmax(260px,0.85fr)',
        gap: 28,
        alignItems: 'stretch',
        padding: 18,
        background: theme.c.fg,
        color: theme.c.bg,
        borderRadius: theme.radii.lg,
        border: `1px solid ${theme.c.fg}`,
        textDecoration: 'none',
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '18px 12px 18px 18px' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 22,
            fontFamily: theme.fonts.mono,
            fontSize: 11,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            color: 'rgba(242,235,223,0.68)',
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: theme.c.accent,
              display: 'inline-block',
            }}
          />
          {b.latestLabel}
        </div>
        <h2
          style={{
            fontFamily: theme.fonts.display,
            fontSize: 40,
            fontWeight: 650,
            letterSpacing: 0,
            lineHeight: 1.06,
            margin: 0,
            maxWidth: 720,
          }}
        >
          {post.title}
        </h2>
        <p
          style={{
            margin: '18px 0 0',
            fontFamily: theme.fonts.body,
            fontSize: 17,
            lineHeight: 1.6,
            color: 'rgba(242,235,223,0.74)',
            maxWidth: 640,
          }}
        >
          {post.description}
        </p>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'center',
            marginTop: 28,
            fontFamily: theme.fonts.mono,
            fontSize: 11,
            color: 'rgba(242,235,223,0.64)',
          }}
        >
          <span>{formatPostDate(post.publishedAt, locale)}</span>
          <span>{post.readingTimeMin} {b.readingTime}</span>
        </div>
      </div>
      <div
        style={{
          minHeight: 260,
          borderRadius: theme.radii.md,
          background:
            'linear-gradient(135deg, rgba(200,85,61,0.95), rgba(63,107,74,0.88))',
          display: 'grid',
          placeItems: 'center',
          padding: 24,
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 142,
            height: 142,
            borderRadius: 28,
            display: 'grid',
            placeItems: 'center',
            background: 'rgba(255,252,245,0.16)',
            border: '1px solid rgba(255,252,245,0.24)',
            color: theme.c.accentFg,
            fontSize: 82,
            lineHeight: 1,
          }}
        >
          {post.coverEmoji}
        </div>
      </div>
    </Link>
  );
}

export function BlogIndexClient({
  posts,
  locale,
}: {
  posts: Post[];
  locale: Locale;
}) {
  const b = getBlogStrings(locale);
  const [query, setQuery] = useState('');
  const normalizedQuery = normalizeSearch(query);
  const featured = posts[0] ?? null;
  const filtered = useMemo(
    () => posts.filter((post) => postMatches(post, normalizedQuery)),
    [posts, normalizedQuery],
  );
  const listPosts = normalizedQuery
    ? filtered
    : featured
      ? posts.filter((post) => post.slug !== featured.slug || post.locale !== featured.locale)
      : posts;

  return (
    <div className="lp-blog-listing" style={{ display: 'grid', gap: 34 }}>
      {featured && !normalizedQuery ? <FeaturedPost post={featured} locale={locale} /> : null}

      <div
        className="lp-blog-tools"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0,1fr) minmax(260px,390px)',
          gap: 18,
          alignItems: 'end',
        }}
      >
        <div>
          <div
            style={{
              fontFamily: theme.fonts.mono,
              fontSize: 11,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              color: theme.c.fgFaint,
              marginBottom: 8,
            }}
          >
            {normalizedQuery ? b.resultsLabel(filtered.length) : b.allPostsLabel}
          </div>
          <h2
            style={{
              margin: 0,
              fontFamily: theme.fonts.display,
              fontSize: 30,
              fontWeight: 600,
              letterSpacing: 0,
              color: theme.c.fg,
            }}
          >
            {b.eyebrow}
          </h2>
        </div>
        <label style={{ display: 'grid', gap: 8 }}>
          <span
            style={{
              fontFamily: theme.fonts.mono,
              fontSize: 10,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              color: theme.c.fgFaint,
            }}
          >
            {b.searchLabel}
          </span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={b.searchPlaceholder}
            style={{
              width: '100%',
              height: 48,
              borderRadius: 14,
              border: `1px solid ${theme.c.borderStrong}`,
              background: theme.c.surface,
              color: theme.c.fg,
              padding: '0 16px',
              fontFamily: theme.fonts.body,
              fontSize: 15,
              outlineColor: theme.c.accent,
            }}
          />
        </label>
      </div>

      {listPosts.length === 0 ? (
        <p
          style={{
            fontFamily: theme.fonts.body,
            fontSize: 16,
            color: theme.c.fgMuted,
            padding: '24px 0 48px',
            margin: 0,
          }}
        >
          {normalizedQuery ? b.noResults : b.empty}
        </p>
      ) : (
        <ul
          className="lp-blog-grid"
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 20,
          }}
        >
          {listPosts.map((post, index) => (
            <PostCard key={`${post.locale}:${post.slug}`} post={post} locale={locale} index={index} />
          ))}
        </ul>
      )}

      <style>{`
        .lp-blog-card { transition: transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease; }
        .lp-blog-card:hover { transform: translateY(-3px); border-color: ${theme.c.borderStrong}; box-shadow: 0 20px 44px -30px rgba(27,23,20,0.35); }
        .lp-blog-featured { transition: transform 180ms ease, box-shadow 180ms ease; }
        .lp-blog-featured:hover { transform: translateY(-3px); box-shadow: 0 28px 60px -36px rgba(27,23,20,0.55); }
        @media (max-width: 980px) {
          .lp-blog-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .lp-blog-featured { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 760px) {
          .lp-blog-tools { grid-template-columns: 1fr !important; }
          .lp-blog-grid { grid-template-columns: 1fr !important; }
          .lp-blog-featured h2 { font-size: 30px !important; }
        }
      `}</style>
    </div>
  );
}
